
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { SimulationConfig } from '@/types';

interface ControlIslandProps {
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  onReset: () => void;
  config: SimulationConfig;
}

export const ControlIsland: React.FC<ControlIslandProps> = ({ 
  isPlaying, setIsPlaying, onReset, config 
}) => {
  return (
    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 pointer-events-none">
        <motion.div 
          initial={{ y: 50, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          className={`
            flex items-center gap-2 p-2 pointer-events-auto 
            rounded-[2.5rem] border backdrop-blur-2xl transition-all duration-500
            ${config.darkMode 
              ? 'bg-zinc-950/80 border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' 
              : 'bg-white/90 border-zinc-950/5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)]'}
          `}
        >
          <button 
            onClick={() => setIsPlaying(!isPlaying)}
            className={`
              w-16 h-16 rounded-[1.8rem] flex items-center justify-center transition-all duration-300 group relative overflow-hidden border
              ${config.darkMode 
                ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400 shadow-[0_0_30px_rgba(6,182,212,0.15)]' 
                : 'border-cyan-600/30 bg-white text-cyan-600 shadow-xl shadow-cyan-600/5'}
              hover:scale-105 active:scale-95
            `}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={isPlaying ? 'pause' : 'play'}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="relative z-10"
              >
                 {isPlaying ? <Pause className="w-7 h-7 fill-current" /> : <Play className="w-7 h-7 fill-current ml-1" />}
              </motion.div>
            </AnimatePresence>
            {!isPlaying && (
               <motion.div 
                 className="absolute inset-0 bg-white/30"
                 animate={{ opacity: [0, 1, 0] }}
                 transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
               />
            )}
         </button>

          <div className="flex flex-col items-center gap-1 mx-2">
              <div className={`w-1 h-3 rounded-full ${config.darkMode ? 'bg-white/20' : 'bg-zinc-950/10'}`} />
              <div className={`w-1 h-1 rounded-full ${config.darkMode ? 'bg-white/10' : 'bg-zinc-950/5'}`} />
          </div>

          <button 
            onClick={onReset}
            className={`
              w-16 h-16 rounded-[1.8rem] flex items-center justify-center transition-all duration-500 
              hover:rotate-180 active:scale-90
              ${config.darkMode ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100'}
            `}
          >
             <RotateCcw className="w-6 h-6" />
          </button>
       </motion.div>
    </div>
  );
};
